import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User, Settings, LogOut, Shield, Key, ShieldCheck } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import UpdateProfileDialog from "@/components/UpdateProfileDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('user');
      toast.success("Logged out successfully");
      onLogout();
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Logout failed");
    }
  };

  const getInitials = (email) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
            <Avatar className="h-10 w-10 bg-gradient-to-br from-indigo-600 to-blue-600">
              <AvatarFallback className="bg-transparent text-white font-semibold">
                {getInitials(user.email)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{user.email}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {user.role === 'admin' ? (
                  <span className="flex items-center">
                    <Shield className="w-3 h-3 mr-1" />
                    Administrator
                  </span>
                ) : (
                  'User'
                )}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowUpdateProfile(true)} data-testid="update-profile">
            <User className="mr-2 h-4 w-4" />
            <span>Update Profile</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowChangePassword(true)} data-testid="change-password">
            <Key className="mr-2 h-4 w-4" />
            <span>Change Password</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/gdpr-settings')} data-testid="gdpr-settings">
            <ShieldCheck className="mr-2 h-4 w-4" />
            <span>GDPR & Privacy</span>
          </DropdownMenuItem>
          {user.role === 'admin' && (
            <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-panel">
              <Settings className="mr-2 h-4 w-4" />
              <span>Admin Panel</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Log Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showChangePassword && (
        <ChangePasswordDialog
          open={showChangePassword}
          onClose={() => setShowChangePassword(false)}
        />
      )}

      {showUpdateProfile && (
        <UpdateProfileDialog
          user={user}
          open={showUpdateProfile}
          onClose={() => setShowUpdateProfile(false)}
        />
      )}
    </>
  );
};

export default UserMenu;
